import { useMemo } from "react";
import type { HarmonicChannel } from "@/lib/types";
import { BarChartUplot } from "@/components/BarChartUplot";

// Interharmonic spectrum for one channel.
//
// Interharmonics are NOT harmonics — they are the energy in the DFT bins
// *between* consecutive harmonics (per IEC 61000-4-7 §5.6). Interharmonic
// group `h` aggregates every bin between harmonic h and h+1; the subgroup
// excludes the two bins immediately adjacent to the harmonics (those carry
// harmonic spectral leakage, not true interharmonic content). The subgroup
// is therefore always <= the group, drawn here as a line under the bars.
//
// This is a distinct quantity from HarmonicSpectrumChart — anyone unfamiliar
// with IEC 61000-4-7 will conflate the two, so the labelling is explicit.

export function InterharmonicChart({ channel }: { channel: HarmonicChannel }) {
  const group = channel.interharmonic_group;
  const subgroup = channel.interharmonic_subgroup;
  const h1 = channel.harmonics_mag?.[0] ?? 0;

  const data = useMemo(() => {
    if (!group || !subgroup || group.length < 50 || h1 <= 0) return null;
    // Interharmonic group h sits between harmonic h and h+1 — orders 1..49.
    const x: number[] = [];
    const grp: number[] = [];
    const sub: number[] = [];
    for (let h = 1; h <= 49; h++) {
      x.push(h);
      grp.push((group[h - 1] / h1) * 100);
      sub.push((subgroup[h - 1] / h1) * 100);
    }
    return { x, grp, sub };
  }, [group, subgroup, h1]);

  if (channel.harmonic_analysis_skipped_reason || !data) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Interharmonic analysis unavailable
        {channel.harmonic_analysis_skipped_reason
          ? ` — ${channel.harmonic_analysis_skipped_reason}`
          : "."}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Interharmonic group <span className="font-medium">h</span> = energy in
        the DFT bins between harmonic h and h+1 — distinct from the harmonics
        themselves. The subgroup line excludes the bins adjacent to the
        harmonics.
      </p>
      <BarChartUplot
        x={data.x}
        bars={{ label: "interharmonic group", values: data.grp, color: "#a855f7" }}
        line={{ label: "interharmonic subgroup", values: data.sub, color: "#f59e0b" }}
        height={220}
        xLabel="between harmonic h and h+1"
        yLabel="% of H1"
      />
    </div>
  );
}
